import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Download, Search, FileText, ChevronLeft, ChevronRight, AlertCircle, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { useFilters } from "../context/FilterContext";
import ThemeChip from "../components/ThemeChip";
import { PageHeader } from "../components/SectionHeader";
import { appendFyToSearchParams, buildDashboardParams, serializeFyForApi } from "../utils/dashboardFilters";
import { fmtCr } from "../utils/format";
import { applyUplift } from "../utils/sanctionedUplift";

// Sortable column header component
function SortTh({ label, field, sortBy, sortOrder, onSort, className = "" }) {
  const active = sortBy === field;
  return (
    <th
      className={`text-left font-medium text-slate-500 uppercase py-2 px-2 whitespace-nowrap cursor-pointer select-none hover:text-slate-800 hover:bg-slate-100 transition-colors group ${className}`}
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`transition-colors ${active ? "text-[#B71C1C]" : "text-slate-500 group-hover:text-slate-600"}`}>
          {active
            ? sortOrder === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
            : <ChevronsUpDown size={12} />}
        </span>
      </span>
    </th>
  );
}

export default function Grants() {
  const { filters, viewMode, API } = useFilters();
  const [searchParams] = useSearchParams();

  const [grants, setGrants] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [exporting, setExporting] = useState(false);
  const [sortBy, setSortBy] = useState("grant_approved_date");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSort = useCallback((field) => {
    if (sortBy === field) {
      setSortOrder(o => o === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  }, [sortBy]);

  const urlFy = searchParams.get("fy");
  const activeFy = urlFy || serializeFyForApi(filters.fy);

  const params = useMemo(() => ({
    fy: activeFy,
    view_mode: viewMode,
    theme: filters.theme,
    trust: filters.trust,
    status: filters.status,
    search: search || undefined,
    page,
    page_size: 50,
    sort_by: sortBy,
    sort_order: sortOrder,
  }), [activeFy, viewMode, filters.theme, filters.trust, filters.status, search, page, sortBy, sortOrder]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/grants/list`, { params }),
      axios.get(`${API}/grants/summary`, { params: { fy: activeFy, view_mode: viewMode, theme: filters.theme, trust: filters.trust, status: filters.status } }),
    ]).then(([listRes, sumRes]) => {
      setGrants(listRes.data.data || []);
      setTotal(listRes.data.total || 0);
      setTotalPages(listRes.data.total_pages || 1);
      setSummary(sumRes.data || {});
    }).catch(console.error).finally(() => setLoading(false));
  }, [params, API, activeFy, viewMode, filters.theme, filters.trust, filters.status]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const exportParams = new URLSearchParams();
      appendFyToSearchParams(exportParams, activeFy ? activeFy.split(",") : []);
      if (filters.theme) exportParams.append("theme", filters.theme);
      if (filters.trust) exportParams.append("trust", filters.trust);
      if (filters.status) exportParams.append("status", filters.status);
      if (search) exportParams.append("search", search);
      const response = await axios.get(`${API}/grants/export?${exportParams.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `grants_export_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const statusClass = (s) =>
    s === "Active" || s === "Ongoing" ? "bg-green-100 text-green-700" :
    s === "Closed" ? "bg-slate-100 text-slate-500" :
    "bg-amber-100 text-amber-700";

  const hasFilters = activeFy || filters.theme || filters.trust || filters.status || search;

  return (
    <div className="space-y-4" data-testid="grants-page">
      <PageHeader
        title="Grants"
        subtitle={`${total.toLocaleString()} grants · ${fmtCr(applyUplift(summary.total_allocation_cr, filters, viewMode, null, null))} total allocation`}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="text-2xl font-bold text-slate-900">{total.toLocaleString()}</div>
          <div className="text-xs text-slate-500 mt-0.5">Total Grants</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="text-2xl font-bold text-slate-900">{fmtCr(applyUplift(summary.total_allocation_cr, filters, viewMode, null, null))}</div>
          <div className="text-xs text-slate-500 mt-0.5">Total Sanctioned</div>
        </div>
      </div>

      {/* Search bar only + Export */}
      <div className="flex items-center gap-2">
        <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by grantee name, grant ID, or portfolio..."
              className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B71C1C]/20 focus:border-[#B71C1C]"
              data-testid="grants-search-input"
            />
            {searchInput && (
              <button type="button" onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-600">
                <X size={14} />
              </button>
            )}
          </div>
          <button type="submit" className="px-3 py-2 bg-[#B71C1C] text-white rounded-lg text-sm font-medium hover:bg-[#8B1515] transition-colors whitespace-nowrap" data-testid="grants-search-btn">
            Search
          </button>
        </form>

        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-60 transition-colors whitespace-nowrap"
          data-testid="export-btn"
        >
          <Download size={14} />
          {exporting ? "Exporting..." : "Export CSV"}
        </button>
      </div>

      {/* Filter indicator */}
      {hasFilters && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="filter-indicator">
          <AlertCircle size={13} />
          Filters applied: showing {total} of {summary.total_grants || total} grants
        </div>
      )}

      {/* Grants Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B71C1C]" />
          </div>
        ) : grants.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-600">
            <FileText size={40} className="mb-2 opacity-40" />
            <p className="text-sm">No grants found matching your criteria</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="grants-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <SortTh label="Grant ID"       field="grant_id"           sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} className="px-3" />
                  <SortTh label="Project Title"   field="project_title"     sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortTh label="Grantee"         field="grantee_name"      sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortTh label="Portfolio"       field="theme"             sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortTh label="Source of Fund"  field="trust"             sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortTh label="Status"          field="status"            sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortTh label="FY"              field="fy"                sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortTh label="Sanctioned"      field="amount_recommended" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} className="text-right" />
                  <SortTh label="Start Date"      field="project_start_date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortTh label="End Date"        field="project_end_date"   sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                </tr>
              </thead>
              <tbody>
                {grants.map((g, i) => (
                  <tr key={g.grant_id || i} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-slate-50/30"}`}>
                    <td className="py-2 px-3 font-mono text-slate-600 whitespace-nowrap">{g.grant_id || "–"}</td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      <span className="font-medium text-slate-800 max-w-[220px] truncate block" title={g.project_title}>
                        {g.project_title || "–"}
                      </span>
                    </td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      <span className="text-slate-700 max-w-[160px] truncate block" title={g.grantee}>
                        {g.grantee || "–"}
                      </span>
                      {g.grantee_acronym && (
                        <span className="text-slate-600 text-[10px]">{g.grantee_acronym}</span>
                      )}
                    </td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      {g.theme ? <ThemeChip theme={g.theme} /> : <span className="text-slate-500">–</span>}
                    </td>
                    <td className="py-2 px-2 text-slate-600 whitespace-nowrap">{g.trust || "–"}</td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${statusClass(g.status)}`}>
                        {g.status || "–"}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{g.fy || "–"}</td>
                    <td className="py-2 px-2 text-right font-medium text-slate-800 whitespace-nowrap">
                      {g.amount_cr ? fmtCr(g.amount_cr) : "–"}
                    </td>
                    <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{g.start_date ? new Date(g.start_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "–"}</td>
                    <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{g.end_date ? new Date(g.end_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
            <div className="text-xs text-slate-500">
              Showing {((page - 1) * 50) + 1}–{Math.min(page * 50, total)} of {total} grants
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs text-slate-600">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
